import * as XLSX from "xlsx";

export const parseExcelFile = async (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, raw: true, defval: "" });
        const headers = jsonData[0] || [];
        const rows = jsonData.slice(1);

        const formattedData = rows
          .map((row) => {
            const entry = {};
            headers.forEach((header, index) => {
              const columnName = header != null ? header.toString().trim() : "";
              if (columnName === "") return;

              let key = columnName;
              let suffix = 2;
              while (Object.prototype.hasOwnProperty.call(entry, key)) {
                key = `${columnName}_${suffix}`;
                suffix += 1;
              }

              const value = row[index];
              entry[key] = value === undefined || value === "" ? null : value;
            });
            return entry;
          })
          .filter((entry) => Object.keys(entry).length > 0);

        resolve(formattedData);
      } catch (error) {
        console.error("Excel parsing error:", error);
        reject(new Error(`فشل في تحليل ملف Excel: ${error.message}`));
      }
    };

    reader.onerror = () => reject(new Error("فشل في قراءة الملف"));
    reader.readAsArrayBuffer(file);
  });

export const excelToJsonForAI = (excelData, context = "مالي") => {
  if (!excelData || !Array.isArray(excelData) || excelData.length === 0) {
    return "لا توجد بيانات من ملف Excel";
  }

  const sampleSize = Math.min(10, excelData.length);
  const sampleData = excelData.slice(0, sampleSize);
  let summary = `\nبيانات من ملف Excel (${excelData.length} صف):\n`;

  if (sampleData.length > 0) {
    const columns = Object.keys(sampleData[0]);
    summary += `الأعمدة: ${columns.join(", ")}\n`;
    const typeAnalysis = {};

    columns.forEach((column) => {
      const values = sampleData.map((row) => row[column]).filter((value) => value != null);
      if (!values.length) return;
      const firstValue = values[0];
      if (typeof firstValue === "number") typeAnalysis[column] = "رقمي";
      else if (typeof firstValue === "boolean") typeAnalysis[column] = "منطقي";
      else if (firstValue instanceof Date) typeAnalysis[column] = "تاريخ";
      else typeAnalysis[column] = "نصي";
    });

    summary += "أنواع البيانات:\n";
    Object.entries(typeAnalysis).forEach(([column, type]) => {
      summary += `  - ${column}: ${type}\n`;
    });

    summary += "\nقيم نموذجية:\n";
    sampleData.slice(0, 3).forEach((row, index) => {
      const rowValues = Object.entries(row)
        .filter(([, value]) => value != null)
        .map(([key, value]) => `${key}=${value}`)
        .join(", ");
      summary += `صف ${index + 1}: ${rowValues}\n`;
    });

    if (context === "مالي") {
      const financialCols = columns.filter((column) => {
        const value = column.toLowerCase();
        return ["مبلغ", "amount", "قيمة", "value", "تكلفة", "cost", "إيراد", "revenue", "سعر", "price", "ربح", "profit", "خسارة", "loss"].some((keyword) => value.includes(keyword));
      });

      if (financialCols.length > 0) {
        summary += `\nالأعمدة المالية المكتشفة: ${financialCols.join(", ")}\n`;
        financialCols.forEach((column) => {
          const values = sampleData
            .map((row) => {
              const rawValue = row[column];
              if (typeof rawValue === "number") return rawValue;
              if (typeof rawValue === "string") {
                const parsed = parseFloat(rawValue.replace(/[^\d.-]/g, ""));
                return Number.isNaN(parsed) ? null : parsed;
              }
              return null;
            })
            .filter((value) => value != null);

          if (values.length > 0) {
            const sum = values.reduce((acc, current) => acc + current, 0);
            summary += `  ${column}: ${values.length} قيمة، المجموع=${sum.toFixed(2)}, المتوسط=${(sum / values.length).toFixed(2)}\n`;
          }
        });
      }

      const dateCols = columns.filter((column) => {
        const value = column.toLowerCase();
        return ["تاريخ", "date", "يوم", "شهر", "سنة"].some((keyword) => value.includes(keyword));
      });
      if (dateCols.length > 0) {
        summary += `\nالأعمدة الزمنية: ${dateCols.join(", ")}\n`;
      }

      const textCols = columns.filter((column) => {
        const value = column.toLowerCase();
        return ["اسم", "name", "وصف", "description", "نوع", "type", "فئة", "category"].some((keyword) => value.includes(keyword));
      });
      if (textCols.length > 0) {
        summary += `\nالأعمدة النصية: ${textCols.join(", ")}\n`;
        textCols.forEach((column) => {
          const values = sampleData
            .map((row) => row[column])
            .filter((value) => value != null && value.toString().trim() !== "");
          if (values.length > 0) {
            summary += `  ${column}: ${values.length} قيمة، ${new Set(values.map((value) => value.toString())).size} قيمة فريدة\n`;
          }
        });
      }
    }
  }

  return summary;
};

export const exportToExcel = (data, filename = "data.xlsx", sheetName = "Sheet1") => {
  try {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    XLSX.writeFile(workbook, filename);
    return true;
  } catch (error) {
    console.error("Export to Excel error:", error);
    return false;
  }
};

export const analyzeExcelStructure = (excelData) => {
  if (!excelData || !Array.isArray(excelData) || excelData.length === 0) {
    return { error: "لا توجد بيانات" };
  }

  const sample = excelData.slice(0, Math.min(5, excelData.length));
  const columns = Object.keys(sample[0] || {});

  return {
    totalRows: excelData.length,
    totalColumns: columns.length,
    columns: columns.map((column) => {
      const values = sample.map((row) => row[column]);
      const nonEmptyValues = values.filter((value) => value != null && value.toString().trim() !== "");

      return {
        name: column,
        sampleValues: values.slice(0, 3),
        emptyCount: values.length - nonEmptyValues.length,
        dataType: detectDataType(values),
        uniqueValues: new Set(nonEmptyValues.map((value) => value.toString())).size,
      };
    }),
  };
};

const detectDataType = (values) => {
  const nonEmptyValues = values.filter((value) => value != null && value.toString().trim() !== "");
  if (!nonEmptyValues.length) return "فارغ";

  const firstValue = nonEmptyValues[0];
  if (typeof firstValue === "number") return "رقم";

  const date = new Date(firstValue);
  if (!Number.isNaN(date.getTime()) && firstValue.toString().match(/^\d{4}-\d{2}-\d{2}/)) {
    return "تاريخ";
  }

  if (["true", "false"].includes(firstValue.toString().toLowerCase()) || typeof firstValue === "boolean") {
    return "منطقي";
  }

  if (firstValue.toString().match(/^-?\d*\.?\d+$/)) return "رقم (نص)";
  return "نص";
};
